import { Injectable } from '@angular/core';
import { throwError } from 'rxjs';
import {
  CHECKOUT_INACTIVE_OFFER,
  CHECKOUT_PRODUCT_IS_OUT_OF_STOCK,
  CHECKOUT_OFFER_IS_NOT_DELIVERABLE
} from '~shared/constants/invalid-params.constants';
import { ApplicationEventService } from '~shared/modules/application-event/services';
import { ICart, IOfferLine } from '~shared/modules/cart/models';
import { IValidationError } from '~shared/modules/cart/models/validation-error.model';
import { BadRequestHttpError, BaseHttpError, GeneralHttpError } from '~shared/modules/error-handler';
import { INVALID_OFFER_MESSAGE, INVALID_PREODER_REVIEW_ERRORS } from '../constants/offer.constant';
import { NotificationApplicationEvent } from '~shared/modules/application-event/models';
import { GaTrackingEventAction, GaTrackingEventLabel } from '~shared/modules/ga-tracking/constants';

@Injectable({ providedIn: 'root' })
export class OfferHelperService {
  constructor(private readonly appEventService: ApplicationEventService) { }

  handleInvalidOffersError(error: BaseHttpError) {
    const message = this.getErrorMessage(error);

    if (message === INVALID_OFFER_MESSAGE) {
      this.publishExpiredOfferNotification();
    }

    return throwError(error);
  }

  handleInvalidReviewOffersError(e: BaseHttpError) {
    try {
      const hasInvalidError = e.sourceError.error?.errors.some((e) => INVALID_PREODER_REVIEW_ERRORS.includes(e.type));
      if(hasInvalidError) {
        this.publishExpiredOfferNotification();
      }
    } catch(e) {}
    return throwError(e);
  }

  handleInvalidCartOffers(cart: ICart) {
    const offers = this.extractOffersFromCart(cart);

    if (!this.areOffersValid(offers)) {
      this.publishExpiredOfferNotification();
    }
  }

  isOfferValid(offer: IOfferLine) {
    if (!offer.invalidParams || !offer.invalidParams.length) {
      return true;
    }

    return !offer.invalidParams
      .some((invalidParam: IValidationError) => {
        const isInactive = invalidParam.type === CHECKOUT_INACTIVE_OFFER;
        const isOutOfStock = invalidParam.type === CHECKOUT_PRODUCT_IS_OUT_OF_STOCK;
        const isUndeliverable = invalidParam.type === CHECKOUT_OFFER_IS_NOT_DELIVERABLE;

        return isInactive || isOutOfStock || isUndeliverable;
      });
  }

  private getErrorMessage(error: BaseHttpError) {
    if (error instanceof BadRequestHttpError) {
      const invalidParams = error.invalidParams;
      const errorKeys = Object.keys(invalidParams);
      const invalidOfferKey = errorKeys.find(key => {
        return invalidParams[key].reason === INVALID_OFFER_MESSAGE
          || invalidParams[key].type === INVALID_OFFER_MESSAGE
      });

      return invalidOfferKey ? INVALID_OFFER_MESSAGE : '';
    }

    if (error instanceof GeneralHttpError) {
      return error.message;
    }

    return error.title;
  }

  private extractOffersFromCart(cart: ICart): IOfferLine[] {
    return cart.items
      .map(cartItem => cartItem.items)
      .reduce((allOffers, currentOffers) => allOffers.concat(currentOffers), []);
  }

  private areOffersValid(offers: IOfferLine[]) {
    return !offers.some(offer => !this.isOfferValid(offer));
  }

  private publishExpiredOfferNotification() {
    this.appEventService.publish(
      new NotificationApplicationEvent(
        GaTrackingEventAction.CheckoutNotifications,
        GaTrackingEventLabel.ExpiredOffersLabel
      )
    )
  }
}
